/**
 * A first draft of the recommended move set (slice 7; slice 11's roster screen will use the same
 * shape). From a creature's real learnset, up to four moves, each where the learnset allows:
 *
 * 1. STAB attack: one of its own types, in its better attacking category (Atk >= Sp. Atk is
 *    physical), the most power x accuracy (no self-KO; recoil counts 0.9).
 * 2. Coverage attack: another type, super-effective against the most of the 18 types, then power x
 *    accuracy; better category first.
 * 3. A stage-up move for a stat its position uses (then any stat up), else the most accurate status
 *    move.
 * 4. A burst move (Agility, Quick Attack): the most Speed stages.
 *
 * Ties go to the move id, so the draft is stable across extracts. Pure; the pack is passed in.
 */
import {
  ALL_TYPES,
  effectiveness,
  type Creature,
  type Move,
  type Position,
  type StatName,
} from '@tdmons/sim';

type BattleStat = Exclude<StatName, 'hp'>;

/** The stats each position's battles use, most important first (docs/design-plan.md). */
export const POSITION_STATS: Readonly<Record<Position, readonly BattleStat[]>> = {
  QB: ['spa'],
  RB: ['atk', 'spe'],
  WR: ['spe'],
  TE: ['atk', 'def'],
  OL: ['atk', 'def'],
  DL: ['atk'],
  LB: ['atk'],
  CB: ['spe'],
  S: ['spe'],
  BENCH: [],
};

const byId = (x: Move, y: Move) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0);

/** Highest score first, then id. */
function best(moves: readonly Move[], score: (m: Move) => number): Move | undefined {
  let top: Move | undefined;
  let topScore = -Infinity;
  for (const m of [...moves].sort(byId)) {
    const s = score(m);
    if (s > topScore) {
      top = m;
      topScore = s;
    }
  }
  return top;
}

function attackValue(m: Move): number {
  if (m.effect.kind !== 'attack') return -Infinity;
  if (m.effect.selfKo) return -Infinity;
  return m.effect.power * m.effect.accuracy * (m.effect.recoil ? 0.9 : 1);
}

/** How many of the 18 types this attack type hits super-effectively. */
function superEffectiveCount(m: Move): number {
  if (m.effect.kind !== 'attack') return 0;
  const type = m.effect.type;
  return ALL_TYPES.filter((t) => effectiveness(type, [t]) >= 2).length;
}

export function draftMoveset(
  creature: Pick<Creature, 'types' | 'baseStats' | 'learnset'>,
  moves: ReadonlyMap<string, Move>,
  position: Position,
): Move[] {
  const known = creature.learnset.flatMap((id) => {
    const m = moves.get(id);
    return m ? [m] : [];
  });
  const category = creature.baseStats.atk >= creature.baseStats.spa ? 'physical' : 'special';
  const attacks = known.filter((m) => m.effect.kind === 'attack' && !m.effect.selfKo);
  const inCategory = (pool: Move[]) => {
    const preferred = pool.filter(
      (m) => m.effect.kind === 'attack' && m.effect.category === category,
    );
    return preferred.length > 0 ? preferred : pool;
  };
  const typeOf = (m: Move) => (m.effect.kind === 'attack' ? m.effect.type : m.type);

  const stab = best(
    inCategory(attacks.filter((m) => creature.types.includes(typeOf(m)))),
    attackValue,
  );
  const coverage = best(
    inCategory(attacks.filter((m) => !creature.types.includes(typeOf(m)))),
    (m) => superEffectiveCount(m) * 1000 + attackValue(m),
  );

  const stats = POSITION_STATS[position];
  const ups = known.filter((m) => m.effect.kind === 'stage' && m.effect.stages > 0);
  const roleUp = best(
    ups.filter((m) => m.effect.kind === 'stage' && stats.includes(m.effect.stat)),
    (m) =>
      m.effect.kind === 'stage'
        ? (stats.length - stats.indexOf(m.effect.stat)) * 10 + m.effect.stages
        : -Infinity,
  );
  const anyUp = best(ups, (m) => (m.effect.kind === 'stage' ? m.effect.stages : -Infinity));
  const status = best(
    known.filter((m) => m.effect.kind === 'status'),
    (m) => (m.effect.kind === 'status' ? m.effect.accuracy : -Infinity),
  );
  const support = roleUp ?? anyUp ?? status;

  const burst = best(
    known.filter((m) => m.effect.kind === 'burst'),
    (m) => (m.effect.kind === 'burst' ? m.effect.speedStages : -Infinity),
  );

  const out: Move[] = [];
  for (const m of [stab, coverage, support, burst]) {
    if (m && !out.some((x) => x.id === m.id)) out.push(m);
  }
  return out;
}
